import type { Item, Theme } from "@wayflyer/flyui";
import { AutocompleteField, DropdownField, Spinner, Subheading } from "@wayflyer/flyui";
import type { LegacyTableColumn } from "@wayflyer/flyui-dataviz";
import { MetricReferenceDirection, useCsvDownload } from "@wayflyer/flyui-dataviz";
import type { CSSProperties } from "react";

import { MetricCard } from "../../components/metric-card";
import type { UIElement } from "../../utils/types";

import { CardsGrid, Container, LeftSpacer, RightFlexContainer, RowGrid, SortContainer, TitleRow } from "./styled";

export type Tile = {
  title: string | UIElement;
  sortValue?: number | null;
  metricValue?: string | null;
  metricReferenceValue?: string | null;
  metricReferenceSuccess?: boolean;
  metricReferenceText?: string | null;
  metricReferenceDirection?: MetricReferenceDirection;
  tooltip?: string;
  style?: CSSProperties;
  theme?: Theme;
};

interface GroupedTilesHeaderProps {
  title?: React.ReactNode | null;
  rows: AxisConfig;
  columns: AxisConfig;
  tiles: { [row: string]: { [column: string]: Tile } };
}

export type AxisConfigElement = {
  id: string;
  text: string;
  enabled: boolean;
};

export type SelectConfig = {
  dropdownLabel: string;
  onEnable: (enabled: boolean, element: string) => void;
};

export type SortConfig = {
  name: string;
  initialValue: string;
  label?: string;
  prefix?: string;
  options: Item[];
  onChange: (item: Item | null) => void;
};

export type AxisConfig = {
  elements: AxisConfigElement[];
  select?: SelectConfig;
  sort?: SortConfig;
};

export interface GroupedTilesProps {
  title?: React.ReactNode | null;
  rows: AxisConfig;
  columns: AxisConfig;
  tiles: { [row: string]: { [column: string]: Tile } };
  loading?: boolean;
}

const CARD_HEIGHT = 140;

const makeSelectHeader = (config: AxisConfig): UIElement => {
  if (config.select) {
    const msbLabel = config.select.dropdownLabel;
    return (
      <div style={{ minWidth: "200px" }}>
        <AutocompleteField
          name={msbLabel}
          placeholder={msbLabel}
          multi="nowrap"
          value={config.elements.filter((element) => element.enabled).map((element) => element.id)}
          clearable={false}
          options={config.elements.map(({ id, text }) => ({ id, text }))}
          onChange={(values) => {
            config.elements.forEach((element) => {
              const enabled = values.includes(element.id);
              if (enabled !== element.enabled) {
                config.select?.onEnable(enabled, element.id);
              }
            });
          }}
        />
      </div>
    );
  } else {
    return null;
  }
};

const makeSortHeader = (config: AxisConfig): UIElement => {
  if (config.sort) {
    return (
      <>
        {config.sort.prefix}
        <LeftSpacer />
        <SortContainer>
          <DropdownField
            name={config.sort.name}
            placeholder={config.sort.label}
            initialValue={config.sort.initialValue}
            options={config.sort.options}
            onChange={(id) => {
              const item = config.sort.options.find((option) => option.id === id);
              config.sort.onChange(item);
            }}
          />
        </SortContainer>
        <LeftSpacer />
      </>
    );
  } else {
    return null;
  }
};

const GroupedTilesHeader = ({ title, tiles, rows, columns }: GroupedTilesHeaderProps): UIElement => {
  const { data: rawData, columns: rawColumns } = extractRawData(tiles, rows, columns);

  // @ts-expect-error - should update dataviz to accept ReactNode
  const csvButton = useCsvDownload(rawData, title, rawColumns, true, "paid_channels.csv");

  const rowsSelect = makeSelectHeader(rows);
  const rowsSort = makeSortHeader(rows);
  const columnsSelect = makeSelectHeader(columns);
  const columnsSort = makeSortHeader(columns);

  const titleElement = (() => {
    if (!title) {
      return null;
    } else if (typeof title === "string") {
      return <Subheading>{title}</Subheading>;
    } else {
      return title;
    }
  })();

  return (
    <Container>
      <TitleRow>
        {titleElement}
        {columnsSelect}
        {rowsSelect}
        <RightFlexContainer>
          {columnsSort}
          {rowsSort}
          {csvButton ? csvButton : null}
        </RightFlexContainer>
      </TitleRow>
    </Container>
  );
};

interface MetricCardWrapperProps {
  row: AxisConfigElement;
  column: AxisConfigElement;
  tile: Tile;
  loading: boolean;
}

const MetricCardWrapper = ({ row, column, tile, loading }: MetricCardWrapperProps) => {
  if (!row.enabled || !column.enabled) {
    return null;
  }
  return (
    <MetricCard
      title={tile.title}
      metricValue={tile.metricValue}
      metricReferenceValue={tile.metricReferenceValue}
      metricReferenceDirection={tile.metricReferenceDirection}
      metricReferenceSuccess={tile.metricReferenceSuccess}
      metricReferenceText={tile.metricReferenceText}
      tooltip={tile.tooltip}
      loading={loading}
      style={tile.style}
      height={CARD_HEIGHT}
    />
  );
};

interface MetricRowProps {
  row: AxisConfigElement;
  columns: AxisConfig;
  tiles: { [column: string]: Tile };
  loading: boolean;
}

const MetricRow = ({ row, columns, tiles, loading }: MetricRowProps): UIElement => {
  return (
    <RowGrid>
      {columns.elements.map((column) => (
        <MetricCardWrapper
          key={`metric_card_${row.id}_${column.id}`}
          row={row}
          column={column}
          tile={tiles[column.id]}
          loading={loading}
        />
      ))}
    </RowGrid>
  );
};

const extractRawData = (tiles, rows, columns) => {
  const rowColumnName = "ROW";

  const makeColumn = (id, name) => {
    return {
      id,
      name,
      align: "left",
    } as LegacyTableColumn;
  };

  return {
    data: rows.elements.map((row) => {
      const rv = {
        [rowColumnName]: row.text,
      };
      columns.elements.map((column) => {
        rv[column.id] = tiles[row.id][column.id].metricValue;
      });
      return rv;
    }),
    columns: [makeColumn(rowColumnName, "Metric")].concat(
      columns.elements.map((column) => {
        return makeColumn(column.id, column.text);
      })
    ),
  };
};

/**
 * GroupedTiles component
 */
export const GroupedTiles = ({ title = "", rows, columns, tiles, loading = false }: GroupedTilesProps): UIElement => {
  if (!rows.elements.length) {
    return <Spinner />;
  }

  return (
    <Container>
      <GroupedTilesHeader title={title} tiles={tiles} rows={rows} columns={columns} />
      <CardsGrid>
        {rows.elements.map((row) => {
          return (
            <MetricRow
              key={`metric_row_${row.id}`}
              row={row}
              columns={columns}
              tiles={tiles[row.id]}
              loading={loading}
            />
          );
        })}
      </CardsGrid>
    </Container>
  );
};
